import { Controller } from "@hotwired/stimulus";
import throttle from "./utils/throttle";
import * as Sentry from "@sentry/browser";

/**
 * The MenuFilterController handles real-time filtering of menu items and features.
 * It provides a search functionality that:
 * - Filters both menu items and featured items
 * - Filters section titles
 * - Handles accented characters in search
 * - Shows/hides a clear button
 * - Updates section visibility based on matching items or title
 *
 * Required targets:
 * - searchInput: The search input field
 * - clearButton: The button to clear the search
 * - feature: Feature containers to filter
 * - featureTitle, featureName, featureDescription: Searchable text within features
 * - section: Section containers that can be hidden when empty
 * - sectionTitle: The section title text
 * - item: Menu item containers to filter
 * - itemName, itemDescription: Searchable text within menu items
 */
export default class extends Controller {
  static values = {
    mainElementSelector: String,
    stickyRelativeToElementSelector: String,
  };
  static targets = [
    "searchInput",
    "content",
    "section",
    "sectionTitle",
    "item",
    "itemName",
    "itemDescription",
    "itemsContainer",
    "feature",
    "featureTitle",
    "featureName",
    "featureDescription",
    "clearButton",
    "wrapper",
    "lowStockChip",
  ];

  // ADMIN ONLY. The chip target does not exist on the
  // customer menu, so this stays false there and every
  // branch below is a no-op: `filterItems` behaves
  // exactly as it did before the chip existed.
  lowStockOnly = false;

  connect() {
    this.toggleClearButton();
    // The search bar is `turbo_permanent`, so the chip
    // survives a page refresh with its pressed state
    // intact while this controller is rebuilt. Read the
    // state back off the DOM rather than assuming false,
    // or a refresh leaves a pressed chip filtering
    // nothing.
    this.lowStockOnly =
      this.hasLowStockChipTarget &&
      this.lowStockChipTarget.getAttribute("aria-pressed") ===
        "true";
    if (this.lowStockOnly) this.filterItems();
    document.addEventListener("keydown", this.exitSearchMode);

    let mainElement;

    if (this.hasMainElementSelectorValue) {
      mainElement = document.querySelector(this.mainElementSelectorValue);
    } else {
      // Impossible to happen, but we saw a "Crashed in non-app" error that
      // set this to an empty string ("").
      // https://calisto-0e.sentry.io/issues/6585371458/?project=4508627019759616
      mainElement = document.querySelector("main");
      Sentry.captureMessage(
        `mainElementSelectorValue is not set for menu filter controller, 
        value was: ${this.mainElementSelectorValue}`
      );
    }

    if (mainElement) {
      this.boundHandleScroll = throttle(this.handleSearchBarScroll, 100);
      mainElement.addEventListener("scroll", this.boundHandleScroll);
    } else {
      Sentry.captureMessage(
        "mainElement is not found for menu filter controller"
      );
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.exitSearchMode);
    const mainElement = document.querySelector("main");
    if (mainElement) {
      mainElement.removeEventListener("scroll", this.boundHandleScroll);
    }
  }

  /**
   * Narrows the list to items that are low or out of
   * stock. Admin only: the chip that calls this is not
   * rendered on the customer menu.
   */
  toggleLowStock() {
    this.lowStockOnly = !this.lowStockOnly;
    this.reflectLowStockChip();
    this.filterItems();
  }

  /**
   * Turns the chip off if it is on, and reports whether
   * it had to. Returns false when there was nothing to
   * do, which is always the case on the customer menu:
   * `lowStockOnly` cannot be true without a chip to set
   * it.
   */
  resetLowStock() {
    if (!this.lowStockOnly) return false;

    this.lowStockOnly = false;
    this.reflectLowStockChip();
    return true;
  }

  /**
   * The chip's DOM state, in one place. State that
   * clears in the model but not on the button is the
   * version of this bug that comes back.
   */
  reflectLowStockChip() {
    if (!this.hasLowStockChipTarget) return;

    this.lowStockChipTarget.setAttribute(
      "aria-pressed",
      String(this.lowStockOnly)
    );
    this.lowStockChipTarget.classList.toggle(
      "btn-active",
      this.lowStockOnly
    );
  }

  /**
   * The stock half of the filter. The mark lives on the
   * card itself (`data-stock-state`), written by the
   * admin item components; anything without one is
   * either healthy or not tracking stock at all.
   */
  matchesStockFilter(item) {
    if (!this.lowStockOnly) return true;

    const state = item.dataset.stockState;
    return state === "low" || state === "out";
  }

  exitSearchMode = (event) => {
    if (event.key === "Escape") {
      this.clearSearch();
    }
  };

  /**
   * Normalizes text by removing accents and converting to lowercase.
   * This allows for accent-insensitive search (e.g., "cafe" matches "café").
   */
  normalizeText(text) {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  /**
   * Main filtering function that:
   * 1. Filters features based on their title, name, and description
   * 2. Filters menu items within sections
   * 3. Shows sections if their title matches or they have matching items
   * 4. Updates clear button visibility
   */
  filterItems() {
    const searchTerm = this.normalizeText(this.searchInputTarget.value.trim());
    this.toggleClearButton();

    // Filter features
    this.featureTargets.forEach((feature) => {
      const featureTitle = this.normalizeText(
        feature.querySelector('[data-menu-filter-target="featureTitle"]')
          ?.textContent || ""
      );
      const featureName = this.normalizeText(
        feature.querySelector('[data-menu-filter-target="featureName"]')
          ?.textContent || ""
      );
      const featureDescription = this.normalizeText(
        feature.querySelector('[data-menu-filter-target="featureDescription"]')
          ?.textContent || ""
      );

      // A feature is not an item and has no shelf, so
      // it has nothing to say while the list is narrowed
      // to what is running out.
      const isVisible =
        !this.lowStockOnly &&
        (featureTitle.includes(searchTerm) ||
          featureName.includes(searchTerm) ||
          featureDescription.includes(searchTerm));

      feature.classList.toggle("hidden", !isVisible);
    });

    // Group items by their parent section for better organization
    const itemsBySection = new Map();
    this.itemTargets.forEach((item) => {
      const section = item.closest('[data-menu-filter-target="section"]');
      if (!itemsBySection.has(section)) {
        itemsBySection.set(section, []);
      }
      itemsBySection.get(section).push(item);
    });

    // Filter items and update section visibility
    itemsBySection.forEach((items, section) => {
      let hasVisibleItems = false;

      // Check if section title matches
      const sectionTitle = this.normalizeText(
        section.querySelector('[data-menu-filter-target="sectionTitle"]')
          ?.textContent || ""
      );
      const sectionTitleMatches = sectionTitle.includes(searchTerm);

      // A matching section title shows all of its items,
      // as it always has. The stock filter is ANDed on
      // top of that rather than folded into it: a
      // merchant searching "tequila" with the chip on is
      // asking for the tequila that is running out, not
      // for every bottle in the section.
      items.forEach((item) => {
        let matchesSearch = sectionTitleMatches;

        if (!matchesSearch) {
          const itemName = this.normalizeText(
            item.querySelector('[data-menu-filter-target="itemName"]')
              .textContent
          );
          const itemDescription = this.normalizeText(
            item.querySelector('[data-menu-filter-target="itemDescription"]')
              ?.textContent || ""
          );
          matchesSearch =
            itemName.includes(searchTerm) ||
            itemDescription.includes(searchTerm);
        }

        const isVisible =
          matchesSearch && this.matchesStockFilter(item);

        const itemWrapper = item.parentElement;
        itemWrapper.classList.toggle("hidden", !isVisible);

        if (isVisible) hasVisibleItems = true;
      });

      // Show section if title matches or it has visible items.
      // A title match no longer keeps an empty section on
      // screen while the stock filter is on: the merchant
      // asked what is running out, and a section with
      // nothing running out is an answer of "none".
      const keepForTitle = sectionTitleMatches && !this.lowStockOnly;

      section.classList.toggle(
        "hidden",
        !hasVisibleItems && !keepForTitle
      );
    });
  }

  /**
   * "Get me back to the whole list."
   *
   * Bound to Escape and to `sortable:sortingStarted`,
   * and NOT to the clear button, which has
   * `clearSearchWithoutFocus` of its own. That split is
   * why the chip is dropped here:
   *
   * - Escape is a global escape hatch. Clearing the text
   *   while leaving the list filtered hides the reason
   *   most of the menu just vanished, and the merchant
   *   has no reason to connect it to the key they
   *   pressed.
   * - Sorting already clears the search so a drag never
   *   reorders against a list with rows missing. A chip
   *   that survived would put that hazard straight back.
   * - The clear button belongs to the text box. Clearing
   *   one control should not silently reset another
   *   sitting next to it, and the chip is right there
   *   saying what it does.
   *
   * The re-filter is conditional because the old `else`
   * branch never re-rendered: with the chip on and the
   * box empty, resetting the state alone would leave
   * every hidden card hidden.
   */
  clearSearch() {
    const clearedChip = this.resetLowStock();
    const hadText =
      this.searchInputTarget.value.trim().length > 0;

    if (hadText) this.searchInputTarget.value = "";
    if (hadText || clearedChip) this.filterItems();

    if (hadText) {
      this.searchInputTarget.focus();
    } else {
      this.searchInputTarget.blur();
    }
  }

  /**
   * Toggles the visibility of the clear button based on whether
   * there is text in the search input or the input is focused.
   * Uses opacity and pointer-events for smooth transitions.
   */
  toggleClearButton() {
    const hasText = this.searchInputTarget.value.trim().length > 0;
    const isFocused = document.activeElement === this.searchInputTarget;
    const shouldShow = hasText || isFocused;

    this.clearButtonTarget.classList.toggle("opacity-0", !shouldShow);
    this.clearButtonTarget.classList.toggle("pointer-events-none", !shouldShow);
  }

  /**
   * Shows the clear button when the input is focused, regardless of text content
   */
  showClearButtonOnFocus() {
    this.toggleClearButton();
  }

  /**
   * Hides the clear button when input loses focus (unless there is text)
   */
  hideClearButtonOnBlur() {
    this.toggleClearButton();
  }

  /**
   * Handles clearing the search input with different behaviors based on focus state:
   *
   * Use cases:
   * 1. When input is focused and has text:
   *    - Clears the text
   *    - Maintains focus on input
   * 2. When input is focused but has no text:
   *    - Blurs the input (exits search mode)
   * 3. When input is not focused but has text:
   *    - Clears the text
   *    - Does NOT focus the input
   *    - Updates clear button visibility
   *
   * Note: Uses mousedown instead of click to prevent focus-related issues
   */
  clearSearchWithoutFocus(event) {
    // Prevent any default behavior and stop propagation
    event.preventDefault();
    event.stopPropagation();

    const wasFocused = document.activeElement === this.searchInputTarget;
    const hadText = this.searchInputTarget.value.trim().length > 0;

    this.searchInputTarget.value = "";
    this.filterItems();

    if (wasFocused && hadText) {
      // Case 1: Focused + has text = clear and keep focus
      this.searchInputTarget.focus();
    } else if (wasFocused && !hadText) {
      // Case 2: Focused + no text = exit search mode
      this.searchInputTarget.blur();
    } else {
      // Case 3: Not focused = just clear and update button visibility
      this.toggleClearButton();
    }
  }

  /**
   * Handles the search bar's appearance when scrolling (desktop/tablet only)
   * This method works in conjunction with specific classes in content_component.rb
   *
   * Behavior:
   * - Mobile: No effect (early return)
   * - Desktop/Tablet:
   *   - When sticky (scrolled): Adds background, shadow, and border
   *   - When not sticky: Removes background, moves shadow to input
   *
   * Note: Uses inline styles for background to properly override Tailwind's bg-transparent
   * Important: If modifying the search bar styling in content_component.rb,
   * ensure the changes don't break this scroll behavior
   */
  handleSearchBarScroll = () => {
    if (window.isSorting) {
      return;
    }

    if (!this.hasWrapperTarget) {
      return;
    }

    // We need to consider any navbars at the top, like it currently exists
    // for the users side
    const topScreenNavbar = document.getElementById("top_screen_navbar");
    const topScreenNavbarRect = topScreenNavbar?.getBoundingClientRect();

    const wrapper = this.wrapperTarget;

    let rectTop;
    if (this.hasStickyRelativeToElementSelectorValue) {
      const stickyRelativeToElement = document.querySelector(
        this.stickyRelativeToElementSelectorValue
      );
      if (stickyRelativeToElement) {
        const stickyRelativeToElementRect =
          stickyRelativeToElement.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();

        rectTop = wrapperRect.top - stickyRelativeToElementRect.top;
      } else {
        Sentry.captureMessage(
          "stickyRelativeToElement is not found for menu filter controller"
        );
        // Using default behavior
        const rect = wrapper.getBoundingClientRect();
        rectTop = topScreenNavbar
          ? rect.top - topScreenNavbarRect.height
          : rect.top;
      }
    } else {
      // Default behavior
      const rect = wrapper.getBoundingClientRect();
      rectTop = topScreenNavbar
        ? rect.top - topScreenNavbarRect.height
        : rect.top;
    }

    // Calculate sticky threshold from the computed top position
    const computedStyle = window.getComputedStyle(wrapper);
    const stickyOffset = parseInt(computedStyle.top, 10) || 0;

    const isSticky = rectTop <= stickyOffset;

    if (isSticky) {
      this.showNavbarBackground();
    } else {
      this.hideNavbarBackground();
    }
  };

  showNavbarBackground = () => {
    // Toggle background color using CSS custom property for theme compatibility

    this.wrapperTarget.style.setProperty(
      "background-color",
      "var(--fallback-b1,oklch(var(--b3)/var(--tw-bg-opacity, 1)))"
    );
    this.wrapperTarget.classList.add("shadow-md");
    this.wrapperTarget.classList.add("border-b");
    // Toggle shadow between input and wrapper based on scroll position
    this.searchInputTarget.classList.remove("shadow-md");
  };

  hideNavbarBackground = () => {
    this.wrapperTarget.style.removeProperty("background-color");
    this.wrapperTarget.classList.remove("shadow-md");
    this.wrapperTarget.classList.remove("border-b");
    // Toggle shadow between input and wrapper based on scroll position
    this.searchInputTarget.classList.add("shadow-md");
  };
}
